"use client";
import { useEffect, useState } from "react";
import CarCard from "@/app/components/CarCard";
import Reveal from "@/app/components/Reveal";
import { Car } from "@/types/Car";
import axios from "axios";
import {
  Loader2,
  ShieldCheck,
  Star,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const whyChooseUs = [
  {
    icon: ShieldCheck,
    title: "No Hidden Fees",
    description: "Transparent pricing, always. What you see is what you pay.",
  },
  {
    icon: Star,
    title: "Top Rated Service",
    description: "Trusted by thousands of happy customers across Malawi.",
  },
  {
    icon: CalendarCheck,
    title: "Easy Booking",
    description: "Reserve your ride in minutes — quick & hassle-free.",
  },
];

const reviews = [
  {
    initial: "S",
    name: "Sarah M.",
    rating: 5,
    quote:
      "Great service and excellent car! The booking process was simple and the car was in perfect condition. Highly recommend!",
  },
  {
    initial: "J",
    name: "James K.",
    rating: 5,
    quote:
      "Booked a car for a weekend trip to Zomba — smooth pickup, clean vehicle, and the price was exactly what was quoted upfront.",
  },
  {
    initial: "T",
    name: "Thoko B.",
    rating: 4,
    quote:
      "Very reliable service. Had a small delay at pickup but the team communicated well and sorted it quickly.",
  },
];

export default function VehicleSection() {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewIndex, setReviewIndex] = useState(0);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await axios.get(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/cars`,
        );
        setCars(res.data);
      } catch (err) {
        console.error("Failed to fetch cars:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const nextReview = () => setReviewIndex((i) => (i + 1) % reviews.length);
  const prevReview = () =>
    setReviewIndex((i) => (i - 1 + reviews.length) % reviews.length);

  return (
    <section className="px-4 py-12 bg-white">
      <div className="mx-auto max-w-7xl">
        {/* ── Popular Cars ── */}
        <h2 className="mb-6 text-2xl font-bold text-blue-900">Popular Cars</h2>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-blue-800 animate-spin" />
          </div>
        ) : cars.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cars.map((car) => (
              <CarCard key={car._id} car={car} />
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">
            No cars available at the moment.
          </p>
        )}

        {/* ── Why Choose Us ── */}
        <div className="mt-16 rounded-3xl bg-blue-50/50 p-6 md:p-10">
          <Reveal>
            <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wider text-blue-600">
              Our Promise
            </p>
            <h2 className="mb-8 text-center text-2xl font-bold text-blue-900 md:text-3xl">
              Why Choose Us?
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {whyChooseUs.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 100}>
                  <div className="group flex h-full flex-col items-center rounded-2xl border border-gray-100 bg-white p-6 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 transition-colors duration-300 group-hover:bg-blue-700">
                      <Icon className="h-7 w-7 text-blue-700 transition-colors duration-300 group-hover:text-white" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-gray-500">
                      {item.description}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* ── Customer Reviews ── */}
        <div className="mt-10">
          <Reveal>
            <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wider text-blue-600">
              Verified Feedback
            </p>
            <h2 className="mb-8 text-center text-2xl font-bold text-blue-900 md:text-3xl">
              What Our Customers Say
            </h2>
          </Reveal>

          <Reveal className="mx-auto max-w-2xl">
            <div className="relative rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < reviews[reviewIndex].rating
                          ? "text-amber-400 fill-amber-400"
                          : "text-gray-200 fill-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs font-semibold text-gray-700">
                  {reviews[reviewIndex].rating.toFixed(1)}
                </span>
                <span className="text-xs text-gray-400">· Verified review</span>
              </div>

              <blockquote className="relative border-l-2 border-blue-100 pl-4">
                <p className="text-sm leading-relaxed text-gray-600">
                  &ldquo;{reviews[reviewIndex].quote}&rdquo;
                </p>
                <footer className="mt-4 flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                    {reviews[reviewIndex].initial}
                  </div>
                  <span className="text-xs font-semibold text-gray-700">
                    {reviews[reviewIndex].name}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    · Verified customer
                  </span>
                </footer>
              </blockquote>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={prevReview}
                  aria-label="Previous review"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-blue-600 hover:text-blue-700"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex gap-1.5">
                  {reviews.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setReviewIndex(i)}
                      aria-label={`Go to review ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === reviewIndex
                          ? "w-5 bg-blue-700"
                          : "w-1.5 bg-gray-200"
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextReview}
                  aria-label="Next review"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-blue-600 hover:text-blue-700"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
